/**
 * t368u: HR records, stage 1. Shared by the API (authoritative validation) and the three apps.
 * Bank details and the NI number are encrypted by the API; every response carries masked hints only, and the real
 * value comes back from the reveal endpoint, which writes an `hr.sensitive.revealed` audit entry.
 */
import { HR_DOC_TYPE_SHORT, type HrDocType } from './hrDocuments';
import { FORM_FIELD_LABELS, normaliseFormValue, type FormField, type FormValue } from './forms';

export const HR_SECTIONS = ['personal', 'address', 'emergency', 'bank', 'tax'] as const;
export type HrSection = (typeof HR_SECTIONS)[number];
/** Custom fields sit in one of the built-in sections or at the end, under "Additional information". */
export type HrFieldSection = HrSection | 'additional';
export const HR_FIELD_SECTIONS: HrFieldSection[] = [...HR_SECTIONS, 'additional'];
export const HR_SECTION_LABEL: Record<HrFieldSection, string> = {
  personal: 'Personal details', address: 'Home address', emergency: 'Emergency contacts', bank: 'Bank details', tax: 'Tax and NI', additional: 'Additional information',
};

export interface HrSettings {
  enabled: boolean; sections: Record<HrSection, boolean>;
  /** t373u: documents the "Complete your details" onboarding step asks everyone to upload (none by default). */
  ask_documents: HrDocType[];
}
export const HR_DEFAULT_SETTINGS: HrSettings = { enabled: false, sections: { personal: true, address: true, emergency: true, bank: true, tax: true }, ask_documents: [] };
export const hrEnabledSections = (s: HrSettings): HrSection[] => (s.enabled ? HR_SECTIONS.filter(k => s.sections[k] !== false) : []);

/** Custom field types: the channel-form types that make sense on a record (files are HR documents, t373u). */
export const HR_FIELD_TYPES = ['short_text', 'long_text', 'number', 'single_select', 'multi_select', 'date', 'checkbox'] as const;
export type HrFieldType = (typeof HR_FIELD_TYPES)[number];
export const HR_FIELD_TYPE_LABELS: Record<HrFieldType, string> = Object.fromEntries(HR_FIELD_TYPES.map(t => [t, FORM_FIELD_LABELS[t]])) as Record<HrFieldType, string>;
/** What the person can do with the field on their own record. */
export type HrStaffAccess = 'hidden' | 'view' | 'edit' | 'request';
export const HR_STAFF_ACCESS_LABEL: Record<HrStaffAccess, string> = { hidden: 'Hidden from them', view: 'They can see it', edit: 'They can edit it', request: 'They can ask to change it' };
/** Which permission a manager needs to see the field. */
export type HrManagerView = 'hr.view' | 'hr.confidential.view';
export const HR_MANAGER_VIEW_LABEL: Record<HrManagerView, string> = { 'hr.view': 'Anyone who can see HR records', 'hr.confidential.view': 'Only people who can see confidential details' };

export interface HrFieldDef {
  id: string; label: string; type: HrFieldType; help: string | null; options: string[]; section: HrFieldSection;
  required: boolean; in_onboarding: boolean; sensitive: boolean; staff_access: HrStaffAccess; manager_view: HrManagerView;
  position: number; archived_at: string | null;
}
export type HrFieldInput = Partial<Omit<HrFieldDef, 'id' | 'archived_at'>> & { archived?: boolean };
export const HR_FIELD_LIMITS = { fields: 60, label: 80, help: 300, options: 50, optionLength: 100 } as const;

/** A custom field as a channel-form field, so values go through the same validation. */
export const hrFieldAsForm = (f: Pick<HrFieldDef, 'id' | 'label' | 'type' | 'options'>): FormField => ({ id: f.id, label: f.label, type: f.type, options: f.options, required: false });
export function normaliseHrFieldValue(f: HrFieldDef, raw: unknown): [FormValue | null, string | null] {
  if (raw === null || raw === undefined || raw === '' || (Array.isArray(raw) && !raw.length)) return [null, null];
  const [v, err] = normaliseFormValue(hrFieldAsForm(f), raw);
  return err ? [null, err] : [v ?? null, null];
}
export function validateHrField(b: HrFieldInput, existing?: HrFieldDef): string[] {
  const errs: string[] = [];
  const label = (b.label ?? existing?.label ?? '').trim();
  const type = b.type ?? existing?.type;
  if (!label || label.length > HR_FIELD_LIMITS.label) errs.push(`Label must be 1-${HR_FIELD_LIMITS.label} characters`);
  if (!type || !(HR_FIELD_TYPES as readonly string[]).includes(type)) errs.push('Pick a field type');
  if ((b.help ?? '').length > HR_FIELD_LIMITS.help) errs.push('Help text is too long');
  if (type === 'single_select' || type === 'multi_select') {
    const o = b.options ?? existing?.options ?? [];
    if (!o.length) errs.push('Add at least one option');
    if (o.length > HR_FIELD_LIMITS.options) errs.push('Too many options');
    if (o.some(x => typeof x !== 'string' || !x.trim() || x.length > HR_FIELD_LIMITS.optionLength)) errs.push(`Options must be 1-${HR_FIELD_LIMITS.optionLength} characters`);
    if (new Set(o).size !== o.length) errs.push('Options must be different');
  }
  if (b.section !== undefined && !HR_FIELD_SECTIONS.includes(b.section)) errs.push('Pick a section');
  if (b.staff_access !== undefined && !['hidden', 'view', 'edit', 'request'].includes(b.staff_access)) errs.push('Pick what the person can do');
  if (b.manager_view !== undefined && !['hr.view', 'hr.confidential.view'].includes(b.manager_view)) errs.push('Pick who can see it');
  const sens = b.sensitive ?? existing?.sensitive;
  if (sens && (type === 'multi_select' || type === 'checkbox')) errs.push('Tick boxes and multi selects can\'t be marked sensitive');
  return errs;
}

// ---------------------------------------------------------------------------------------------- formats (en-GB)
const digits = (s: string) => s.replace(/[\s\u2010-\u2015-]/g, '');
/** UK sort code: six digits, shown 12-34-56. */
export function normaliseSortCode(raw: string): string | null {
  const d = digits(raw ?? '');
  return /^\d{6}$/.test(d) ? d : null;
}
export const formatSortCode = (d: string) => `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4, 6)}`;
/** UK account number: eight digits (seven-digit numbers are padded with a leading 0, as banks do). */
export function normaliseAccountNumber(raw: string): string | null {
  const d = digits(raw ?? '');
  if (/^\d{8}$/.test(d)) return d;
  if (/^\d{7}$/.test(d)) return `0${d}`;
  return null;
}
/** Building society roll number (optional): letters, digits, spaces, dots, slashes and dashes. */
export function normaliseRollNumber(raw: string | null | undefined): string | null | undefined {
  const s = (raw ?? '').trim().toUpperCase();
  if (!s) return null;
  return /^[A-Z0-9 ./-]{1,18}$/.test(s) ? s : undefined;
}
/**
 * National Insurance number: two prefix letters, six digits, suffix A-D. D, F, I, Q, U and V never appear in the prefix,
 * O never second, and BG, GB, KN, NK, NT, TN and ZZ are never issued.
 */
export function normaliseNiNumber(raw: string): string | null {
  const s = (raw ?? '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z]\d{6}[A-D]$/.test(s)) return null;
  if (['BG', 'GB', 'KN', 'NK', 'NT', 'TN', 'ZZ'].includes(s.slice(0, 2))) return null;
  return s;
}
export const formatNiNumber = (s: string) => `${s.slice(0, 2)} ${s.slice(2, 4)} ${s.slice(4, 6)} ${s.slice(6, 8)} ${s.slice(8)}`;
/** UK postcode, normalised to upper case with one space before the last three characters (SW1A 1AA). */
export function normalisePostcode(raw: string): string | null {
  const s = (raw ?? '').replace(/\s+/g, '').toUpperCase();
  if (!/^([A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}|GIR0AA)$/.test(s)) return null;
  return `${s.slice(0, -3)} ${s.slice(-3)}`;
}
export const isUkCountry = (c: string | null | undefined) => !c || /^(uk|gb|united kingdom|great britain|england|scotland|wales|northern ireland)$/i.test(c.trim());
export const isValidPhone = (s: string) => /^\+?[\d\s()-]{7,20}$/.test(s.trim()) && digits(s).replace(/[()+]/g, '').length >= 7;
export const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

/** Masked hints kept in plain text next to the ciphertext. */
export const maskSortCode = (last2: string) => `••-••-${last2}`;
export const maskAccountNumber = (last4: string) => `••••${last4}`;
export const maskNiNumber = (last3: string) => `•• •• •• ${last3.slice(0, 2)} ${last3.slice(2)}`;
/** How long a revealed value stays on screen before it is hidden again. */
export const HR_REVEAL_SECONDS = 30;

// ---------------------------------------------------------------------------------------------- the record
export const STARTER_STATEMENTS = ['A', 'B', 'C'] as const;
export type StarterStatement = (typeof STARTER_STATEMENTS)[number];
export const STARTER_STATEMENT_LABEL: Record<StarterStatement, string> = {
  A: 'A: this is my first job since 6 April, with no benefits or pensions',
  B: 'B: this is my only job now, but I\'ve had another since 6 April',
  C: 'C: I have another job or a pension',
};
export const STUDENT_LOAN_PLANS = ['none', 'plan_1', 'plan_2', 'plan_4', 'plan_5'] as const;
export type StudentLoanPlan = (typeof STUDENT_LOAN_PLANS)[number];
export const STUDENT_LOAN_LABEL: Record<StudentLoanPlan, string> = { none: 'No student loan', plan_1: 'Plan 1', plan_2: 'Plan 2', plan_4: 'Plan 4 (Scotland)', plan_5: 'Plan 5' };

export interface HrPersonal {
  legal_first_name: string | null; legal_middle_names: string | null; legal_last_name: string | null; preferred_name: string | null;
  pronouns: string | null; personal_email: string | null; personal_phone: string | null; nationality: string | null;
}
/**
 * t567u: legal first and last name, personal email and personal phone follow the person's main details (their
 * name, sign-in email and profile phone) until someone types something different. Nothing is copied: a blank HR
 * value means "use the profile", worked out whenever the record is read, so later profile changes come through.
 * Saving the profile value (or clearing the box) keeps it following the profile; anything else is kept as typed.
 */
export const HR_PROFILE_KEYS = ['legal_first_name', 'legal_last_name', 'personal_email', 'personal_phone'] as const;
export type HrProfileKey = (typeof HR_PROFILE_KEYS)[number];
export interface HrProfileSource { display_name: string | null; email: string | null; phone: string | null }
export const HR_FROM_PROFILE = 'From profile';
const NAME_PARTICLES = new Set(['van', 'von', 'de', 'der', 'den', 'del', 'della', 'da', 'di', 'du', 'la', 'le', 'st', 'st.', 'ter', 'ten', 'al', 'el', 'bin', 'ibn']);
/** "Sam O'Neil" → Sam / O'Neil; "Anna van der Berg" → Anna / van der Berg; one word → first name only. */
export function hrSplitName(name: string | null | undefined): { first: string | null; last: string | null } {
  const w = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!w.length) return { first: null, last: null };
  if (w.length === 1) return { first: w[0]!, last: null };
  let i = w.length - 1;
  while (i > 1 && NAME_PARTICLES.has(w[i - 1]!.toLowerCase())) i--;
  return { first: w[0]!, last: w.slice(i).join(' ') };
}
/** What each of the four fields shows when the HR record leaves it blank. */
export function hrProfileDefaults(p: HrProfileSource | null | undefined): Record<HrProfileKey, string | null> {
  const n = hrSplitName(p?.display_name);
  const clean = (v: string | null | undefined) => (v && v.trim() ? v.trim().replace(/\s+/g, ' ') : null);
  const email = clean(p?.email)?.toLowerCase() ?? null;
  const phone = clean(p?.phone);
  return { legal_first_name: n.first, legal_last_name: n.last, personal_email: email && isValidEmail(email) ? email : null, personal_phone: phone && isValidPhone(phone) ? phone : null };
}
const same = (k: HrProfileKey, a: string | null | undefined, b: string | null | undefined) => {
  const x = (a ?? '').trim().replace(/\s+/g, ' '), y = (b ?? '').trim().replace(/\s+/g, ' ');
  if (!x || !y) return false;
  return k === 'personal_email' ? x.toLowerCase() === y.toLowerCase() : k === 'personal_phone' ? x.replace(/[\s()-]/g, '') === y.replace(/[\s()-]/g, '') : x === y;
};
/** True when a typed value is just the profile value (so it is stored as blank and keeps following the profile). */
export const hrIsProfileValue = (k: HrProfileKey, v: string | null | undefined, defaults: Partial<Record<HrProfileKey, string | null>> | null | undefined) => same(k, v, defaults?.[k]);
/** The stored HR values with blanks filled from the profile, and which fields are following it. */
export function hrEffectivePersonal(stored: HrPersonal, defaults: Partial<Record<HrProfileKey, string | null>> | null | undefined): { personal: HrPersonal; from_profile: HrProfileKey[] } {
  const personal = { ...stored };
  const from: HrProfileKey[] = [];
  for (const k of HR_PROFILE_KEYS) {
    const v = stored[k];
    if (v && v.trim()) continue;
    const d = defaults?.[k];
    if (d) { personal[k] = d; from.push(k); }
  }
  return { personal, from_profile: from };
}

export interface HrAddress { line1: string | null; line2: string | null; town: string | null; county: string | null; postcode: string | null; country: string | null }
export interface HrEmergencyContact { id?: string; name: string; relationship: string | null; phone: string; alt_phone: string | null }
export interface HrBankView {
  on_file: boolean; account_name: string | null; sort_code: string | null; account_number: string | null; roll_number_set: boolean;
  updated_at: string | null; updated_by_name: string | null;
  /** Previous accounts, newest first (masked). */
  history: { account_name: string; sort_code: string; account_number: string; from: string; to: string | null; changed_by_name: string | null }[];
}
export interface HrTaxView {
  ni_number: string | null; ni_on_file: boolean; tax_code: string | null; starter_statement: StarterStatement | null;
  student_loan_plan: StudentLoanPlan | null; postgraduate_loan: boolean | null; p45_received: boolean | null;
}
export interface HrFieldValueView { field_id: string; value: FormValue | null; set: boolean; can_edit: 'direct' | 'request' | null }

/** Fields that the person changes through an approval once something is on file (the first entry goes straight in). */
export const HR_APPROVAL_FIELDS = ['legal_first_name', 'legal_middle_names', 'legal_last_name', 'ni_number', 'starter_statement', 'student_loan_plan', 'postgraduate_loan'] as const;
export type HrChangeSection = 'legal_name' | 'ni_number' | 'starter' | 'bank' | 'field';
export const HR_CHANGE_LABEL: Record<HrChangeSection, string> = { legal_name: 'Legal name', ni_number: 'NI number', starter: 'Starter checklist', bank: 'Bank details', field: 'Field' };
export type HrChangeStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export interface HrChangeRequest {
  id: string; user_id: string; user_name: string; section: HrChangeSection; field_id: string | null; label: string;
  /** Masked summary of the new values, e.g. { sort_code: '••-••-56', account_number: '••••1234' }. */
  summary: Record<string, string | null>;
  status: HrChangeStatus; requested_at: string; requested_by_name: string | null;
  decided_at: string | null; decided_by_name: string | null; decision_note: string | null; can_decide: boolean;
}

export interface HrRecord {
  user_id: string; display_name: string; self: boolean;
  sections: HrSection[];
  /** t567u: what's shown, with blanks filled from the profile (see hrEffectivePersonal). */
  personal: HrPersonal; address: HrAddress; emergency: HrEmergencyContact[];
  /** t567u: which personal fields are following the profile, and the profile values themselves (for the edit form). */
  from_profile?: HrProfileKey[]; profile_defaults?: Partial<Record<HrProfileKey, string | null>>;
  bank: HrBankView | null; tax: HrTaxView | null;
  fields: HrFieldDef[]; values: HrFieldValueView[];
  pending: HrChangeRequest[];
  complete: boolean; missing: string[];
  /** t413u: the day they left (deactivated) and when their HR data was erased, if it was. */
  left_on?: string | null; erased_at?: string | null;
  updated_at: string | null;
  can: {
    /** Edit the record directly (managers with hr.edit; the person for the parts they edit themselves). */
    edit: boolean; edit_bank: boolean; reveal: boolean; approve: boolean; approve_bank: boolean;
    /** The person editing their own record: which approval fields are already on file (so changes go for approval). */
    needs_approval: string[];
  };
}
export interface HrRecordUpdate {
  personal?: Partial<HrPersonal>; address?: Partial<HrAddress>; emergency?: HrEmergencyContact[];
  bank?: { account_name: string; sort_code: string; account_number: string; roll_number?: string | null } | null;
  tax?: { ni_number?: string | null; tax_code?: string | null; starter_statement?: StarterStatement | null; student_loan_plan?: StudentLoanPlan | null; postgraduate_loan?: boolean | null; p45_received?: boolean | null };
  values?: Record<string, FormValue | null>;
}
export interface HrUpdateResult { record: HrRecord; requested: string[] }
export interface HrSettingsView extends HrSettings {
  can_manage: boolean; can_view_others: boolean; can_edit_others: boolean; encryption_ready: boolean;
  /** t373u: hr.documents.view / hr.documents.manage somewhere (the Expiring soon list; reminders need manage). */
  can_view_documents: boolean; can_manage_documents: boolean;
  /** t413u: stages 3 and 4 (anywhere; the server re-checks per person). */
  can_payroll?: boolean; can_reports?: boolean; can_remind?: boolean; can_approve?: boolean; can_subject_access?: boolean; can_retention?: boolean; can_keys?: boolean;
}
export type HrRevealField = 'sort_code' | 'account_number' | 'roll_number' | 'ni_number' | 'field';
export interface HrRevealInput { field: HrRevealField; field_id?: string; request_id?: string }
export interface HrPersonRow {
  user_id: string; display_name: string; avatar_path: string | null; location_name: string | null;
  complete: boolean; missing: number; bank_on_file: boolean; ni_on_file: boolean; pending: number;
}
export const HR_EMERGENCY_MAX = 3;

/** Kinds that always reach the person (no mute, sent by email straight away even if read): a bank change could be fraud. */
export const SECURITY_NOTIFICATION_KINDS = ['hr.bank_changed'] as const;
export const isSecurityNotification = (kind: string) => (SECURITY_NOTIFICATION_KINDS as readonly string[]).includes(kind);

/** Muted text for the record list: "Complete", "2 details missing", "1 change to approve". */
export function hrStatusText(r: Pick<HrPersonRow, 'complete' | 'missing' | 'pending'>): string {
  if (r.pending) return `${r.pending} change${r.pending === 1 ? '' : 's'} to approve`;
  if (r.complete) return 'Complete';
  return `${r.missing} detail${r.missing === 1 ? '' : 's'} missing`;
}

/** What still needs filling in for the record to count as complete (the onboarding step ticks itself once this is empty). */
export function hrMissing(r: { personal: HrPersonal; address: HrAddress; emergency: unknown[]; bank_on_file: boolean; ni_on_file: boolean; tax: Pick<HrTaxView, 'starter_statement' | 'student_loan_plan'> | null },
  sections: HrSection[], requiredFields: { label: string; set: boolean }[], askedDocuments: HrDocType[] = []): string[] {
  const out: string[] = [];
  const has = (v: string | null | undefined) => Boolean(v && v.trim());
  if (sections.includes('personal')) {
    if (!has(r.personal.legal_first_name)) out.push('Legal first name');
    if (!has(r.personal.legal_last_name)) out.push('Legal last name');
  }
  if (sections.includes('address')) {
    if (!has(r.address.line1)) out.push('Address');
    if (!has(r.address.town)) out.push('Town or city');
    if (!has(r.address.postcode)) out.push('Postcode');
  }
  if (sections.includes('emergency') && !r.emergency.length) out.push('An emergency contact');
  if (sections.includes('bank') && !r.bank_on_file) out.push('Bank details');
  if (sections.includes('tax')) {
    if (!r.ni_on_file) out.push('NI number');
    if (!r.tax?.starter_statement) out.push('Starter checklist statement');
    if (!r.tax?.student_loan_plan) out.push('Student loan');
  }
  for (const f of requiredFields) if (!f.set) out.push(f.label);
  // t373u: documents the workspace asks for that aren't uploaded yet (already filtered to what's missing).
  for (const t of askedDocuments) out.push(`${HR_DOC_TYPE_SHORT[t]} (upload)`);
  return out;
}

// ---------------------------------------------------------------------------------------------- what the apps show
/** t567u: the muted line under a personal field in the edit form ("From profile" while it follows the profile). */
export function hrProfileHint(k: HrProfileKey, typed: string | null | undefined, defaults: Partial<Record<HrProfileKey, string | null>> | null | undefined): string | undefined {
  const d = defaults?.[k];
  if (!d) return undefined;
  if (!typed || !typed.trim() || hrIsProfileValue(k, typed, defaults)) return HR_FROM_PROFILE;
  return 'Clear to use the profile';
}

/** One line of a record as the three apps show it: a label, the (masked) value, and what Show reveals. */
export interface HrViewRow { key: string; label: string; value: string; reveal?: HrRevealInput; muted?: boolean; /** t567u: plain muted text after the value, e.g. "From profile". */ note?: string }
export interface HrViewSection { id: HrFieldSection; title: string; rows: HrViewRow[]; note?: string }
const dash = '—';
const show = (v: string | null | undefined) => (v && String(v).trim() ? String(v) : dash);
/** A custom field's value as text (en-GB dates, Yes/No for tick boxes). */
export function hrFieldText(f: Pick<HrFieldDef, 'type'>, v: FormValue | null | undefined): string {
  if (v === null || v === undefined || v === '') return dash;
  if (Array.isArray(v)) return v.length ? v.join(', ') : dash;
  if (f.type === 'checkbox') return v ? 'Yes' : 'No';
  if (f.type === 'date' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return String(v);
}
/** "12 Oct 2026, 14:05" in Europe/London. */
export const hrWhen = (iso: string) => new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** The record as sections of rows, in the order every app shows them; custom fields join their section. */
export function hrViewSections(rec: HrRecord): HrViewSection[] {
  const out: HrViewSection[] = [];
  const custom = (id: HrFieldSection): HrViewRow[] => rec.fields.filter(f => f.section === id).map(f => {
    const v = rec.values.find(x => x.field_id === f.id);
    if (f.sensitive) return { key: `field:${f.id}`, label: f.label, value: v?.set ? '••••••' : dash, reveal: v?.set ? { field: 'field', field_id: f.id } : undefined }; // anyone who sees a sensitive field may reveal it (audited)
    return { key: `field:${f.id}`, label: f.label, value: hrFieldText(f, v?.value) };
  });
  const p = rec.personal, a = rec.address;
  if (rec.sections.includes('personal')) {
    const legal = [p.legal_first_name, p.legal_middle_names, p.legal_last_name].filter(Boolean).join(' ');
    const from = new Set(rec.from_profile ?? []);
    const fromNote = (k: HrProfileKey) => (from.has(k) ? HR_FROM_PROFILE : undefined);
    const legalNote = from.has('legal_first_name') && (from.has('legal_last_name') || !p.legal_last_name) ? HR_FROM_PROFILE
      : from.has('legal_last_name') && !from.has('legal_first_name') ? 'Last name from profile' : from.has('legal_first_name') ? 'First name from profile' : undefined;
    out.push({ id: 'personal', title: HR_SECTION_LABEL.personal, rows: [
      { key: 'legal_name', label: 'Legal name', value: show(legal), note: legalNote },
      { key: 'preferred_name', label: 'Preferred name', value: show(p.preferred_name) },
      { key: 'pronouns', label: 'Pronouns', value: show(p.pronouns) },
      { key: 'personal_email', label: 'Personal email', value: show(p.personal_email), note: fromNote('personal_email') },
      { key: 'personal_phone', label: 'Personal phone', value: show(p.personal_phone), note: fromNote('personal_phone') },
      { key: 'nationality', label: 'Nationality', value: show(p.nationality) },
      ...custom('personal')] });
  }
  if (rec.sections.includes('address')) {
    const lines = [a.line1, a.line2, a.town, a.county, a.postcode, isUkCountry(a.country) ? null : a.country].filter(Boolean).join(', ');
    out.push({ id: 'address', title: HR_SECTION_LABEL.address, rows: [{ key: 'address', label: 'Address', value: show(lines) }, ...custom('address')] });
  }
  if (rec.sections.includes('emergency')) {
    const rows: HrViewRow[] = rec.emergency.map((c, i) => ({ key: `contact:${c.id ?? i}`, label: c.relationship ? `${c.name} (${c.relationship})` : c.name, value: [c.phone, c.alt_phone].filter(Boolean).join(' or ') }));
    out.push({ id: 'emergency', title: HR_SECTION_LABEL.emergency, rows: rows.length ? [...rows, ...custom('emergency')] : [{ key: 'none', label: 'Contacts', value: 'None added', muted: true }, ...custom('emergency')] });
  }
  if (rec.bank) {
    const b = rec.bank;
    const reveal = rec.can.reveal;
    out.push({ id: 'bank', title: HR_SECTION_LABEL.bank, note: b.updated_at ? `Changed ${hrWhen(b.updated_at)}${b.updated_by_name ? ` by ${b.updated_by_name}` : ''}` : undefined, rows: b.on_file ? [
      { key: 'account_name', label: 'Account name', value: show(b.account_name) },
      { key: 'sort_code', label: 'Sort code', value: b.sort_code!, reveal: reveal ? { field: 'sort_code' } : undefined },
      { key: 'account_number', label: 'Account number', value: b.account_number!, reveal: reveal ? { field: 'account_number' } : undefined },
      ...custom('bank')] : [{ key: 'none', label: 'Bank account', value: 'Not added yet', muted: true }, ...custom('bank')] });
  }
  if (rec.tax) {
    const t = rec.tax;
    out.push({ id: 'tax', title: HR_SECTION_LABEL.tax, rows: [
      { key: 'ni_number', label: 'NI number', value: t.ni_number ?? dash, reveal: t.ni_on_file && rec.can.reveal ? { field: 'ni_number' } : undefined },
      { key: 'tax_code', label: 'Tax code', value: show(t.tax_code) },
      { key: 'starter_statement', label: 'Starter checklist', value: t.starter_statement ? `Statement ${t.starter_statement}` : dash },
      { key: 'student_loan_plan', label: 'Student loan', value: t.student_loan_plan ? STUDENT_LOAN_LABEL[t.student_loan_plan] : dash },
      { key: 'postgraduate_loan', label: 'Postgraduate loan', value: t.postgraduate_loan == null ? dash : t.postgraduate_loan ? 'Yes' : 'No' },
      { key: 'p45_received', label: 'P45 received', value: t.p45_received == null ? dash : t.p45_received ? 'Yes' : 'No' },
      ...custom('tax')] });
  }
  const extra = custom('additional');
  if (extra.length) out.push({ id: 'additional', title: HR_SECTION_LABEL.additional, rows: extra });
  return out;
}
/** A pending change in one line: "Bank details: ••-••-56, ••••1234 · asked 12 Oct 2026, 14:05". */
/**
 * t370u: what a pending change shows the person looking at it. Their own: Cancel. Someone else's: Approve/Reject only
 * with "Edit staff HR details" (hr.edit, `can.approve`), else nothing at all (read-only, hidden rather than greyed).
 */
export const hrPendingTitle = (self: boolean, canApprove: boolean) => (self || !canApprove ? 'Waiting for approval' : 'Changes to approve');
export function hrPendingControls(self: boolean, canApprove: boolean, canDecide: boolean): 'cancel' | 'decide' | 'needs_bank' | null {
  if (self) return 'cancel';
  if (!canApprove) return null;
  return canDecide ? 'decide' : 'needs_bank';
}
export const hrChangeLine = (c: Pick<HrChangeRequest, 'label' | 'summary' | 'requested_at'>) =>
  `${c.label}: ${Object.values(c.summary).filter(Boolean).join(', ') || 'new details'} · asked ${hrWhen(c.requested_at)}`;
/** What a save that went (partly) for approval says. */
export const hrRequestedText = (requested: string[]) =>
  requested.length ? `Sent for approval: ${requested.map(s => (HR_CHANGE_LABEL as Record<string, string>)[s] ?? s).join(', ').toLowerCase()}. Your manager will check it.` : 'Saved';

/** t368u: problems with what's typed in an HR edit, shown before Save on web, phone web and iOS (the API checks again). */
export function hrDraftErrors(d: HrRecordUpdate, defaults?: Partial<Record<HrProfileKey, string | null>> | null): string[] {
  const e: string[] = [];
  const own = (k: HrProfileKey) => { const v = d.personal?.[k]; return v && !hrIsProfileValue(k, v, defaults) ? v : null; };
  if (own('personal_email') && !isValidEmail(own('personal_email')!)) e.push('Personal email doesn’t look right');
  if (own('personal_phone') && !isValidPhone(own('personal_phone')!)) e.push('Personal phone doesn’t look right');
  if (d.address?.postcode && isUkCountry(d.address.country) && !normalisePostcode(d.address.postcode)) e.push('Enter a UK postcode, like LL11 1AA');
  for (const c of d.emergency ?? []) {
    if (!c.name.trim() && !c.phone.trim()) continue;
    if (!c.name.trim()) e.push('Each emergency contact needs a name');
    if (!isValidPhone(c.phone)) e.push(`${c.name || 'Emergency contact'}: enter a phone number`);
  }
  if (d.bank) {
    if (!d.bank.account_name.trim()) e.push('Enter the name on the account');
    if (!normaliseSortCode(d.bank.sort_code)) e.push('Sort code is 6 digits, like 12-34-56');
    if (!normaliseAccountNumber(d.bank.account_number)) e.push('Account number is 8 digits');
  }
  if (d.tax?.ni_number && !normaliseNiNumber(d.tax.ni_number)) e.push('NI number looks like QQ 12 34 56 C');
  return e;
}
